import React, { ReactElement, useEffect, useState } from 'react';

import {
  Drawer,
  Grid,
  Modal,
  ModalProps,
  notification,
  Steps,
  Typography,
} from 'antd';
import { PersonalInfoForm } from './PersonalInfoForm';
import { useParams } from 'react-router-dom';
import {
  useFetchWaitingListSurveyInstances,
  useUpdateFanAttendeesInfo,
} from '@seaters/data-access';
import {
  AttendeesInfoFormValidator,
  SurveyExtensionPoint,
  getExperienceImageUrl,
} from '@seaters/constants';
import {
  FGOStepItemIdEnum,
  HostStepItemIdEnum,
  StepItemIdEnum,
  StepType,
} from './types';
import { SurveyForm } from './SurveyForm';
import { useFormSteps } from './hooks/useFormSteps';
import { useTranslation } from 'react-i18next';
import { AttendeeInfoStep } from './AttendeeInfoStep';
import { PaymentStep } from './PaymentStep';
import { baseColor300 } from '@seaters/ui';
import { PurchaseSummary } from '../PurchaseSummary';

const { useBreakpoint } = Grid;
const { Title, Text } = Typography;

export interface CheckoutModalProps extends ModalProps {
  children?: ReactElement;
  isDisabled?: boolean;
  isLoading?: boolean;
  backgroundColor?: string;
}

export function CheckoutModal({
  open,
  backgroundColor,
  ...props
}: CheckoutModalProps) {
  const [currentStepKey, setCurrentStepKey] = useState<StepItemIdEnum>(
    StepItemIdEnum.PERSONAL_INFO_FORM
  );
  const { t } = useTranslation();
  const { waitingListId = '' } = useParams();

  const { xs } = useBreakpoint();

  const { data: surveyInstancesAtCheckout } =
    useFetchWaitingListSurveyInstances({
      waitinglist_id: waitingListId,
      extension_point: SurveyExtensionPoint.AT_CHECKOUT,
    });

  const { data: surveyInstancesBeforePayment } =
    useFetchWaitingListSurveyInstances({
      waitinglist_id: waitingListId,
      extension_point: SurveyExtensionPoint.BEFORE_PAYMENT,
    });

  const surveyInstances = surveyInstancesAtCheckout?.content.length
    ? surveyInstancesAtCheckout
    : surveyInstancesBeforePayment;

  const surveyStep: StepType<StepItemIdEnum> = {
    key: StepItemIdEnum.SURVEY_FORM,
    title: (
      <Text aria-current={currentStepKey === StepItemIdEnum.SURVEY_FORM}>
        {t('survey_title_text')}
      </Text>
    ),
    content: (props) => (
      <SurveyForm
        {...props}
        ticketsAmount={waitingList?.position.numberOfSeats}
        surveyInstances={surveyInstances}
      />
    ),
    submitText: t('finish_button_text'),
    orderIndex: 2,
  };

  const paymentStep: StepType<StepItemIdEnum> = {
    key: StepItemIdEnum.PAYMENT_FORM,
    title: (
      <Text aria-current={currentStepKey === StepItemIdEnum.PAYMENT_FORM}>
        {t('payment_modal_title')}
      </Text>
    ),
    content: (props) => {
      return <PaymentStep {...props} />;
    },
    submitText: t('purchase_button_text'),
    orderIndex: 4,
  };

  const personalInfoStep: StepType<StepItemIdEnum> = {
    key: StepItemIdEnum.PERSONAL_INFO_FORM,
    title: (
      <Text aria-current={currentStepKey === StepItemIdEnum.PERSONAL_INFO_FORM}>
        {t('checkout_billinginfo_steptitle')}
      </Text>
    ),
    content: (props) => {
      return <PersonalInfoForm {...props} />;
    },
    submitText: t('button_text_next'),
    orderIndex: 0,
  };

  const attendeeInfoStep: StepType<StepItemIdEnum> = {
    key: StepItemIdEnum.ATTENDEE_INFO,
    title: (
      <Text aria-current={currentStepKey === StepItemIdEnum.ATTENDEE_INFO}>
        {t('checkout_requiredinfo_steptitle')}
      </Text>
    ),
    content: (props) => {
      return <AttendeeInfoStep currentStep={currentStep} {...props} />;
    },
    submitText: t('button_text_next'),
    orderIndex: 3,
  };

  const steps = [personalInfoStep, attendeeInfoStep, paymentStep, surveyStep];

  const [stepsVisibility, setStepsVisibility] = useState<{
    [key in StepItemIdEnum]: boolean;
  }>({
    personalInfoForm: true,
    attendeeInfo: false,
    paymentForm: false,
    surveyStep: false,
  });

  const { mutate: updateFanAttendeesInfo } =
    useUpdateFanAttendeesInfo(waitingListId);

  const filteredSteps = steps
    .filter((el) => stepsVisibility[el.key])
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const {
    nextStep,
    prevStep,
    isLastStep,
    isLoading,
    waitingList,
    currentStep,
  } = useFormSteps(
    filteredSteps,
    undefined,
    undefined,
    setCurrentStepKey as (
      stepKey: StepItemIdEnum | FGOStepItemIdEnum | HostStepItemIdEnum
    ) => void
  );

  const handleAttendeeForm = (attendeeValues: AttendeesInfoFormValidator[]) => {
    updateFanAttendeesInfo(
      { body: { attendees: attendeeValues } },
      {
        onSuccess: () => {
          nextStep();
        },
        onError: (err) => {
          notification.error({
            message: t('notification_error_attendees_info_send'),
            description: err.message,
          });
        },
      }
    );
  };

  // Called, not rendered as a component: a component declared in this body is a new type on every
  // render, so React would remount the step and wipe what the fan has typed into its form.
  const stepContent = filteredSteps[currentStep]?.content({
    next: nextStep,
    prev: prevStep,
    isLastStep,
    waitingList,
    handleAttendeeForm,
  });

  useEffect(() => {
    if (waitingList) {
      setStepsVisibility({
        ...stepsVisibility,
        [StepItemIdEnum.SURVEY_FORM]: surveyInstances
          ? !!surveyInstances.content.length
          : false,
        [StepItemIdEnum.ATTENDEE_INFO]:
          !!waitingList.eventRequiredAttendeeInfo?.length,
        [StepItemIdEnum.PAYMENT_FORM]: !waitingList.freeWaitingList,
      });
    }
  }, [waitingList, surveyInstancesAtCheckout, surveyInstancesBeforePayment]);

  if (xs) {
    return (
      <Drawer
        open={open}
        className="checkout-modal"
        data-testid="checkoutModal"
        onClose={() =>
          props.onCancel?.(
            {} as React.MouseEvent<HTMLButtonElement, MouseEvent>
          )
        }
        placement="bottom"
        height={'100dvh'}
        maskClosable={false}
        styles={{
          mask: {
            backgroundColor: 'transparent',
          },
          header: {
            display: 'none',
          },
          body: {
            padding: 0,
            backgroundColor: backgroundColor || '#f3f6ff',
          },
        }}
      >
        <div className="flex flex-col h-full">
          {/* Summary Section */}
          <div
            style={{
              backgroundColor: baseColor300,
              padding: 16,
            }}
            className="flex flex-col gap-4 relative"
          >
            {/* Header with title and close button */}
            <div className="flex items-center justify-between w-full">
              <Title
                level={2}
                className="text-2xl! font-semibold! leading-8! m-0!"
              >
                {t('checkout_title')}
              </Title>
              <button
                onClick={props.onCancel}
                className="flex items-center justify-center size-9 rounded-full bg-[rgba(0,0,0,0.25)] border-0 cursor-pointer p-0"
                style={{ minWidth: 36, minHeight: 36 }}
                aria-label={
                  t('general_close-btn', { defaultValue: 'Close' }) || 'Close'
                }
              >
                <span className="text-base">✕</span>
              </button>
            </div>

            {/* Event Image and Title */}
            <div className="flex gap-2 items-center w-full mt-[14px]">
              <img
                width={64}
                height={32}
                src={getExperienceImageUrl(waitingList)}
                alt="event"
                className="rounded object-cover"
              />
              <Text className="!text-base !font-semibold !leading-6 !m-0">
                {waitingList?.translatedExperienceName}
              </Text>
            </div>

            {/* Purchase Summary */}
            <PurchaseSummary />
          </div>

          {/* Form Content Section */}
          <div className="flex-1 ">
            <div
              className="flex flex-col gap-4"
              style={{ padding: '20px 16px' }}
            >
              {filteredSteps.length > 1 ? (
                <Steps
                  current={currentStep}
                  items={filteredSteps.map(({ key, title }, index) => ({
                    key,
                    title: index === currentStep ? title : '',
                  }))}
                  responsive={false}
                  size="small"
                />
              ) : null}

              {stepContent}
            </div>
          </div>
        </div>
      </Drawer>
    );
  }
  return (
    <Modal
      open={open}
      className="checkout-modal"
      data-testid="checkoutModal"
      title={
        <Title level={2} className="text-2xl! font-semibold! leading-8! m-0!">
          {t('checkout_title')}
        </Title>
      }
      footer={false}
      width={1024}
      maskClosable
      onCancel={props.onCancel}
      styles={{
        body: {
          alignItems: 'flex-start',
          display: 'flex',
        },
      }}
    >
      <div className="flex flex-1 flex-col gap-6">
        {filteredSteps.length > 1 ? (
          <Steps
            current={currentStep}
            items={filteredSteps.map(({ key, title }) => ({ key, title }))}
            size="small"
            className="mb-6"
          />
        ) : null}
        <div className="flex gap-6 justify-center">{stepContent}</div>
      </div>
      <aside
        style={{
          backgroundColor: baseColor300,
          marginLeft: 32,
          padding: 24,
          width: 336,
        }}
        className="flex flex-col gap-6 !rounded-3xl ml-8"
      >
        <PurchaseSummary />
      </aside>
    </Modal>
  );
}
